/**
 * Landing page content keys — the canonical DEFAULT copy.
 *
 * Every user-facing string on the landing page lives here as a content key.
 * The live page reads from the `site_content` store by key and falls back to
 * these defaults whenever a key has no stored override.
 *
 * COUNSEL-sensitive strings are flagged with `counsel: true` so the platform
 * editor can surface them. Counsel's edits are editor changes, not code changes.
 *
 * Reconciled waitlist copy rules baked into these defaults:
 * Copy rules baked into these defaults:
 *  - card scheme naming (Mastercard) authorised by the operator — trademark
 *    attribution must stay in the footer disclaimer
 *  - patent claims cite the exact granted number: US11,682,002B2
 *  - no present-tense issuance ("your card ships") claims

 */

import styleWithCat from "@/assets/style-with-cat.jpg";
import styleWithDog from "@/assets/style-with-dog.jpg";
import heroVideoAsset from "@/assets/cardmoji-hero.mp4.asset.json";
import heroPosterAsset from "@/assets/cardmoji-hero-poster-v3.jpg.asset.json";

// Lovable asset routes are served by the hosted asset edge, not Vite's local
// SPA fallback. Absolute URLs keep media working in both live preview and the
// published/custom-domain app.
const ASSET_ORIGIN = "https://www.cardmoji.com";
const HERO_VIDEO_URL = `${ASSET_ORIGIN}${heroVideoAsset.url}`;
const HERO_POSTER_URL = `${ASSET_ORIGIN}${heroPosterAsset.url}`;

/** "text" keys are copy; "image"/"video" keys are uploadable media assets. */
export type ContentKeyKind = "text" | "image" | "video";

export type ContentKeyDef = {
  key: string;
  label: string;
  group: string;
  default: string;
  multiline?: boolean;
  counsel?: boolean;
  kind?: ContentKeyKind;
  /**
   * Set when NO screen renders this key today. An editor reviewing and approving
   * wording that reaches no customer, with no way to tell which is which, is the
   * defect — so the reason is shown in the editor. Two shapes:
   *  - "split": the screen was broken into single-purpose steps and this line
   *    moved; the value names the key that carries the statement now.
   *  - "never placed": the wording was written but never put on a screen.
   */
  orphan?: string;
};


export const LANDING_SECTION = "landing";


export const LANDING_CONTENT: ContentKeyDef[] = [
  // ---------------------------------------------------------------- nav
  { key: "landing.nav.brand", label: "Brand wordmark", group: "Navigation", default: "cardmoji" },
  { key: "landing.nav.how", label: "Nav — how it works", group: "Navigation", default: "How it works" },
  { key: "landing.nav.creators", label: "Nav — creators", group: "Navigation", default: "Creators" },
  { key: "landing.nav.banks", label: "Nav — issuing banks", group: "Navigation", default: "Issuing banks" },
  { key: "landing.nav.about", label: "Nav — about", group: "Navigation", default: "About & patent" },
  { key: "landing.nav.login", label: "Nav — log in button", group: "Navigation", default: "Log in" },
  { key: "landing.nav.cta", label: "Nav — primary CTA", group: "Navigation", default: "Create" },

  // ---------------------------------------------------------------- hero
  { key: "landing.hero.badge", label: "Hero badge", group: "Hero", counsel: true, default: "US patent granted" },
  { key: "landing.hero.headline.line1", label: "Headline line 1", group: "Hero", default: "Your face." },
  { key: "landing.hero.headline.line2", label: "Headline line 2", group: "Hero", default: "Your card." },
  {
    key: "landing.hero.subcopy",
    label: "Hero subcopy",
    group: "Hero",
    multiline: true,
    counsel: true,
    default:
      "Take a selfie, pick a style, and get a real prepaid Mastercard with a cartoon you on the front. Thirty seconds to see it. No app to download.",
  },
  { key: "landing.cta.primary", label: "Hero primary CTA", group: "Hero", default: "Try it out — free preview" },
  { key: "landing.cta.secondary", label: "Hero secondary CTA", group: "Hero", default: "How it works" },
  {
    key: "landing.hero.reassurance",
    label: "Hero reassurance line",
    group: "Hero",
    multiline: true,
    counsel: true,
    default: "See your avatar before you pay a cent. {feeOne} for one card, {feeExtra} for each extra.",
  },

  { key: "landing.hero.styles.count", label: "Style-count label", group: "Hero", default: "33 styles" },
  { key: "landing.hero.styles.link", label: "See-all styles link", group: "Hero", default: "See all 33 →" },

  // ------------------------------------------------------- how it works
  { key: "landing.howitworks.eyebrow", label: "Eyebrow", group: "How it works", default: "How it works" },
  {
    key: "landing.howitworks.heading",
    label: "Heading",
    group: "How it works",
    default: "Three steps, about a minute.",
  },
  { key: "landing.howitworks.step1.title", label: "Step 1 title", group: "How it works", default: "Choose a style" },
  {
    key: "landing.howitworks.step1",
    label: "Step 1 body",
    group: "How it works",
    multiline: true,
    default:
      "Tennis, golf, dancing, fiesta, your dog, your cat — 33 to pick from, and you can change your mind later.",
  },
  { key: "landing.howitworks.step2.title", label: "Step 2 title", group: "How it works", default: "Upload a selfie" },
  {
    key: "landing.howitworks.step2",
    label: "Step 2 body",
    group: "How it works",
    multiline: true,
    default:
      "One clear photo of your face. Front camera is fine — we'll tell you what works and what gets sent back.",
  },
  { key: "landing.howitworks.step3.title", label: "Step 3 title", group: "How it works", default: "Create your avatar" },
  {
    key: "landing.howitworks.step3",
    label: "Step 3 body",
    group: "How it works",
    multiline: true,
    default: "We draw you into the style you picked. Don't like it? Regenerate — five goes on every card, free.",
  },
  {
    key: "landing.howitworks.footnote",
    label: "Footnote",
    group: "How it works",
    multiline: true,
    counsel: true,
    default:
      "You only pay once you've seen your card and you're happy with it. Nothing is charged until the bank approves.",
  },

  { key: "landing.howitworks.cta", label: "Footnote CTA", group: "How it works", default: "Start with a style" },

  // ------------------------------------------------------------ creators
  { key: "landing.creators.eyebrow", label: "Eyebrow", group: "Creators", default: "Creator network" },
  {
    key: "landing.creators.heading",
    label: "Heading",
    group: "Creators",
    counsel: true,
    default: "Post your card. Take money off your next one.",
  },
  {
    key: "landing.creators.body",
    label: "Body",
    group: "Creators",
    multiline: true,
    counsel: true,
    default:
      "Same deal as every customer, no follower minimum and no application to win. Share your code: your audience gets money off the Cardmoji card fee on their first card, and so do you when a card ordered with your code ships. It's a discount on a card you order, not a cash payout.",
  },
  { key: "landing.creators.cta.primary", label: "Primary CTA", group: "Creators", default: "Join the network" },
  { key: "landing.creators.cta.secondary", label: "Secondary CTA", group: "Creators", default: "Creator log in" },
  /* L15 · COUNSEL-OWNED MONEY CLAIMS. The figures are TOKENS
     ({{inviteeReward}}, {{referrerReward}}) resolved at render from
     program_referral_settings — never typed. The reward is a discount off the
     Cardmoji card fee: no cash, no payout, no PayPal, no minimum, one discount
     per card, and discounts do not stack. A stat whose token cannot be resolved
     is not rendered at all. */
  { key: "landing.creators.stat1.value", label: "Stat 1 value", group: "Creators", counsel: true, default: "{{inviteeReward}}" },
  { key: "landing.creators.stat1.strong", label: "Stat 1 lead", group: "Creators", counsel: true, default: "off for your audience." },
  {
    key: "landing.creators.stat1.rest",
    label: "Stat 1 detail",
    group: "Creators",
    counsel: true,
    default: " Off the Cardmoji card fee, applied at checkout when they use your code.",
  },
  { key: "landing.creators.stat2.value", label: "Stat 2 value", group: "Creators", counsel: true, default: "{{referrerReward}}" },
  {
    key: "landing.creators.stat2.strong",
    label: "Stat 2 lead",
    group: "Creators",
    counsel: true,
    default: "off a card of your own.",
  },
  {
    key: "landing.creators.stat2.rest",
    label: "Stat 2 detail",
    group: "Creators",
    counsel: true,
    default: " Earned when a card ordered with your code ships — a fee discount, not a cash payout.",
  },
  { key: "landing.creators.stat3.value", label: "Stat 3 value", group: "Creators", counsel: true, default: "1" },
  {
    key: "landing.creators.stat3.strong",
    label: "Stat 3 lead",
    group: "Creators",
    counsel: true,
    default: "discount per card you order.",
  },
  {
    key: "landing.creators.stat3.rest",
    label: "Stat 3 detail",
    group: "Creators",
    counsel: true,
    default: " Discounts don't stack, there's no minimum to reach, and you can track codes used in your hub.",
  },

  // --------------------------------------------------------------- banks
  { key: "landing.banks.eyebrow", label: "Eyebrow", group: "Issuing banks", default: "For issuing banks" },
  {
    key: "landing.banks.heading",
    label: "Heading",
    group: "Issuing banks",
    default: "A card programme people actually want to show people.",
  },
  {
    key: "landing.banks.body",
    label: "Body",
    group: "Issuing banks",
    multiline: true,
    counsel: true,
    default:
      "Cardmoji is a patented personalisation layer that sits on top of your existing prepaid rails. You keep the BIN, the compliance and the customer. We bring the reason they signed up — and the organic reach that comes with a card people post.",
  },
  { key: "landing.banks.deck", label: "Open deck link", group: "Issuing banks", default: "Open the Cardmoji deck" },
  { key: "landing.banks.card1.title", label: "Card 1 title", group: "Issuing banks", default: "You keep the rails" },
  {
    key: "landing.banks.card1.body",
    label: "Card 1 body",
    group: "Issuing banks",
    multiline: true,
    default:
      "Your BIN, your KYC, your issuing licence. We integrate as a personalisation and fulfilment layer, not a competing programme.",
  },
  {
    key: "landing.banks.card2.title",
    label: "Card 2 title",
    group: "Issuing banks",
    default: "Acquisition that shares itself",
  },
  {
    key: "landing.banks.card2.body",
    label: "Card 2 body",
    group: "Issuing banks",
    multiline: true,
    default:
      "Every card ships with a share asset and a referral code. Cardholders post their own card — the acquisition channel is the product.",

  },
  {
    key: "landing.banks.card3.title",
    label: "Card 3 title",
    group: "Issuing banks",
    counsel: true,
    default: "Patent-protected",
  },
  {
    key: "landing.banks.card3.body",
    label: "Card 3 body",
    group: "Issuing banks",
    multiline: true,
    counsel: true,
    default:
      "The selfie-to-card-art pipeline is covered by a granted US patent (US11,682,002B2), licensed to programme partners on an exclusive-by-market basis.",
  },
  {
    key: "landing.banks.card4.title",
    label: "Card 4 title",
    group: "Issuing banks",
    default: "Built for approval rates",
  },
  {
    key: "landing.banks.card4.body",
    label: "Card 4 body",
    group: "Issuing banks",
    multiline: true,
    counsel: true,
    default:
      "Non-reloadable, single-load to {loadLimit}, explicit biometric consent and full KYC before authorisation. Declines cost the customer nothing.",

  },
  {
    key: "landing.banks.form.heading",
    label: "Enquiry form heading",
    group: "Issuing banks",
    default: "Talk to us about a programme",
  },
  {
    key: "landing.banks.form.body",
    label: "Enquiry form body",
    group: "Issuing banks",
    multiline: true,
    default:
      "Tell us your BIN sponsor and target market. We reply within two business days with a partnership pack.",
  },
  { key: "landing.banks.form.name.label", label: "Field — name", group: "Issuing banks", default: "Your name" },
  { key: "landing.banks.form.name.placeholder", label: "Placeholder — name", group: "Issuing banks", default: "First and last name" },
  { key: "landing.banks.form.org.label", label: "Field — institution", group: "Issuing banks", default: "Institution" },
  { key: "landing.banks.form.org.placeholder", label: "Placeholder — institution", group: "Issuing banks", default: "Institution name" },
  { key: "landing.banks.form.email.label", label: "Field — work email", group: "Issuing banks", default: "Work email" },
  { key: "landing.banks.form.email.placeholder", label: "Placeholder — email", group: "Issuing banks", default: "me@institution.com" },
  {
    key: "landing.banks.form.note.label",
    label: "Field — enquiry",
    group: "Issuing banks",
    default: "What are you looking to launch?",
  },
  {
    key: "landing.banks.form.note.placeholder",
    label: "Placeholder — enquiry",
    group: "Issuing banks",
    default: "Prepaid gifting programme, US launch Q1, existing BIN with…",
  },
  { key: "landing.banks.form.submit", label: "Submit button", group: "Issuing banks", default: "Send enquiry" },
  { key: "landing.banks.form.sending", label: "Submit — sending", group: "Issuing banks", default: "Sending…" },
  {
    key: "landing.banks.form.sent",
    label: "Submit — sent",
    group: "Issuing banks",
    default: "Sent — we'll reply within two days",
  },
  {
    key: "landing.banks.form.hint",
    label: "Form hint",
    group: "Issuing banks",
    default: "Name, institution and a work email are enough to start.",
  },
  {
    key: "landing.banks.form.sentHint",
    label: "Form hint — after send",
    group: "Issuing banks",
    default: "Enquiry received. A partnership pack is on its way.",
  },

  // --------------------------------------------------------------- about
  { key: "landing.about.eyebrow", label: "Eyebrow", group: "About", default: "About us" },
  {
    key: "landing.about.heading",
    label: "Heading",
    group: "About",
    default: "We think a payment card should look like the person holding it.",
  },
  {
    key: "landing.about.body",
    label: "Body",
    group: "About",
    multiline: true,
    counsel: true,
    default:
      "Cards have been beige rectangles for fifty years. Cardmoji turns a selfie into card art in about a minute, on top of a real prepaid Mastercard programme — the personalisation people want, on rails banks already trust.",
  },
  { key: "landing.about.card1.badge", label: "Card 1 badge", group: "About", counsel: true, default: "Patent granted" },
  {
    key: "landing.about.card1.title",
    label: "Card 1 title",
    group: "About",
    counsel: true,
    default: "US patent — selfie-to-card personalisation",
  },
  {
    key: "landing.about.card1.body",
    label: "Card 1 body",
    group: "About",
    multiline: true,
    counsel: true,
    default:
      "Our method for generating stylised card art from a single user photo, and binding it to an issued payment credential, is protected by a granted United States patent (US11,682,002B2).",
  },
  { key: "landing.about.card2.title", label: "Card 2 title", group: "About", default: "How the card works" },
  {
    key: "landing.about.card2.body",
    label: "Card 2 body (scheme-naming gate)",
    group: "About",
    multiline: true,
    counsel: true,
    default:
      "A prepaid Mastercard issued by our banking partner. Single load up to {loadLimit}, non-reloadable, spendable anywhere Mastercard is accepted. Funds are held in a safeguarded account — not lent, not invested. Designing your card is free and live today; cards themselves are issued once our launch bank partner goes live.",
  },
  { key: "landing.about.card3.title", label: "Card 3 title", group: "About", default: "Your face, your data" },
  {
    key: "landing.about.card3.body",
    label: "Card 3 body",
    group: "About",
    multiline: true,
    counsel: true,
    default:
      "We measure your face only to draw your avatar, only with your explicit consent, and if you don't finish we purge it automatically within 72 hours. We never sell it.",
  },

  // ------------------------------------------------------------- closing
  {
    key: "landing.closing.heading",
    label: "Heading",
    group: "Closing CTA",
    default: "See yourself on a card in thirty seconds.",
  },
  {
    key: "landing.closing.body",
    label: "Body",
    group: "Closing CTA",
    multiline: true,
    default: "Free to try. You'll know whether you want one before you're asked to pay.",

  },
  { key: "landing.closing.cta", label: "CTA", group: "Closing CTA", default: "Try it out" },

  // -------------------------------------------------------------- footer
  {
    key: "landing.disclaimer.lead",
    label: "Disclaimer — bold lead",
    group: "Footer",
    counsel: true,
    default: "Cardmoji is not a bank and not a card issuer.",
  },
  {
    key: "landing.disclaimer",
    label: "Disclaimer — body",
    group: "Footer",
    multiline: true,
    counsel: true,
    default:
      "We design and personalise cards. Cardmoji prepaid cards are issued by our banking partner under licence from Mastercard, which holds the funds and makes all approval decisions. Mastercard is a registered trademark of Mastercard International. Selfie-to-card personalisation is protected by US11,682,002B2.",


  },
  { key: "landing.footer.product.title", label: "Column — product", group: "Footer", default: "Product" },
  { key: "landing.footer.product.gallery", label: "Link — style gallery", group: "Footer", default: "Style gallery" },
  { key: "landing.footer.product.how", label: "Link — how it works", group: "Footer", default: "How it works" },
  { key: "landing.footer.product.creators", label: "Link — creator network", group: "Footer", default: "Creator network" },
  { key: "landing.footer.partners.title", label: "Column — partners", group: "Footer", default: "Partners" },
  { key: "landing.footer.partners.banks", label: "Link — issuing banks", group: "Footer", default: "Issuing banks" },
  { key: "landing.footer.partners.about", label: "Link — about", group: "Footer", default: "About" },
  {
    key: "landing.footer.partners.staff",
    label: "Link — staff sign in",
    group: "Footer",
    default: "Bank or partner staff sign in",
  },
  { key: "landing.footer.legal.title", label: "Column — legal", group: "Footer", default: "Legal" },
  { key: "landing.footer.legal.terms", label: "Link — terms", group: "Footer", counsel: true, default: "Terms of service" },
  { key: "landing.footer.legal.privacy", label: "Link — privacy", group: "Footer", counsel: true, default: "Privacy policy" },
  {
    key: "landing.footer.legal.biometric",
    label: "Link — biometric policy",
    group: "Footer",
    counsel: true,
    default: "Biometric policy",
  },
  {
    key: "landing.footer.legal.tooltip",
    label: "Legal links tooltip",
    group: "Footer",
    default: "Published at launch",
  },

  // ---------------------------------------------------------------- media
  {
    key: "landing.media.hero.video",
    label: "Hero reel video",
    group: "Media",
    kind: "video",
    default: HERO_VIDEO_URL,
  },
  {
    key: "landing.media.hero.poster",
    label: "Hero reel poster frame",
    group: "Media",
    kind: "image",
    default: HERO_POSTER_URL,
  },
  {
    key: "landing.media.style.cat",
    label: "Style tile — cat companion",
    group: "Media",
    kind: "image",
    default: styleWithCat,
  },
  {
    key: "landing.media.style.cat.alt",
    label: "Style tile — cat alt text",
    group: "Media",
    default: "Cat companion style",
  },
  {
    key: "landing.media.style.dog",
    label: "Style tile — dog companion",
    group: "Media",
    kind: "image",
    default: styleWithDog,
  },
  {
    key: "landing.media.style.dog.alt",
    label: "Style tile — dog alt text",
    group: "Media",
    default: "Dog companion style",
  },
];


export const LANDING_DEFAULTS: Record<string, string> = Object.fromEntries(
  LANDING_CONTENT.map((k) => [k.key, k.default])
);
